import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Baseball, Basketball, ChartLineUp, Lightning, ShieldCheck, Lock, Flame } from "@phosphor-icons/react";

const PRODUCTS = [
  {
    id: "mlb",
    label: "Sports Predictions",
    brand: "Accurate Monkey Sports",
    tagline: "MLB + WNBA · daily predictions · 2 analysts · tamper-evident track record",
    description:
      "One subscription covers both Major League Baseball and the WNBA. Bounce-back & momentum patterns from analysts Charlie & Dakota. Every prediction is locked and timestamped before first pitch — no edits, no deletions. Includes MLB (~70% YTD hit rate) and WNBA at no extra cost.",
    price: "$25",
    cadence: "week",
    accent: "#007AFF",
    href: "/sports/mlb",
    cta: "View Sports Predictions",
    badge: "LIVE · MLB + WNBA",
    stat: { label: "Includes", value: "MLB + WNBA" },
    icon: Baseball,
    learnMoreId: "sports-learn",
  },
  {
    id: "stocks",
    label: "Stock Predictions",
    brand: "Accurate Monkey Stocks",
    tagline: "Hand-curated equity calls · entry · 2 targets · active stop",
    description:
      "Mid-cap and large-cap names with full rationale. Each prediction includes entry price, two profit targets, and an active stop. Subscribers get the alert before market open; outcomes tracked in a tamper-evident ledger.",
    price: "$25",
    cadence: "week",
    accent: "#F59E0B",
    href: "/markets/stocks",
    cta: "View Stock Predictions",
    badge: "LIVE",
    stat: { label: "Includes", value: "Long + Short" },
    icon: ChartLineUp,
    learnMoreId: "stocks-learn",
  },
  {
    id: "options",
    label: "Option Predictions",
    brand: "Accurate Monkey Options",
    tagline: "Call/put plays · entry premium · 2 targets · stop · full rationale",
    description:
      "Single-leg call/put plays on highly-liquid underlyings. Every prediction has strike, expiration, entry premium, 2 targets, and a stop. Get alerts the moment a setup hits with full rationale.",
    price: "$25",
    cadence: "week",
    accent: "#A855F7",
    href: "/markets/options",
    cta: "View Option Predictions",
    badge: "LIVE",
    stat: { label: "Includes", value: "Calls + Puts" },
    icon: Lightning,
    learnMoreId: "options-learn",
  },
];

const BUNDLE = {
  label: "All Access",
  price: "$55",
  cadence: "week",
  description: "Sports (MLB + WNBA) + Stocks + Options. Save ~40% vs buying separately.",
};

function ProductCard({ p, idx }) {
  const Icon = p.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.08 }}
      className="group relative bg-[#121212] border rounded-sm overflow-hidden flex flex-col hover:bg-[#161616] transition-colors"
      style={{ borderColor: `${p.accent}33` }}
      data-testid={`product-card-${p.id}`}
    >
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: p.accent }} />
      <div className="p-6 sm:p-7 flex-1">
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-2">
            <Icon size={20} weight="fill" style={{ color: p.accent }} />
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: p.accent }}>{p.label}</div>
          </div>
          <span
            className="text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 rounded-sm border"
            style={{ color: p.accent, borderColor: `${p.accent}66` }}
            data-testid={`product-badge-${p.id}`}
          >
            {p.badge}
          </span>
        </div>
        <h3 className="font-display text-4xl sm:text-5xl leading-tight mb-3 group-hover:text-white">{p.brand}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed mb-5">{p.tagline}</p>
        <p className="text-sm text-zinc-500 leading-relaxed mb-6">{p.description}</p>

        <div className="flex items-end justify-between pt-4 border-t border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.stat.label}</div>
            <div className="font-display text-2xl mt-1" style={{ color: p.accent }} data-testid={`product-stat-${p.id}`}>{p.stat.value}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Pricing</div>
            <div className="font-display text-2xl mt-1">
              <span style={{ color: p.accent }}>{p.price}</span>
              <span className="text-zinc-500 text-base"> /{p.cadence}</span>
            </div>
          </div>
        </div>
      </div>
      <Link
        to={p.href}
        data-testid={`product-cta-${p.id}`}
        className="flex items-center justify-center gap-2 px-6 py-4 font-bold text-sm uppercase tracking-[0.25em] transition-colors border-t group-hover:bg-white"
        style={{ borderColor: `${p.accent}33`, color: p.accent }}
      >
        {p.cta} <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    </motion.div>
  );
}

// HOT-banner copy variants — A/B test. Picks one per session and pins it via
// localStorage so the visitor sees consistent messaging on reload. The
// `data-variant` attribute makes it trivial to wire up conversion analytics
// later (e.g., "how many `subscribe` events did variant B drive vs A?").
const HOT_VARIANTS = [
  {
    id: "a",
    label: "Trial + Save",
    body: (
      <>
        <span className="font-bold">7-day free trial</span> on every plan ·
        <span className="text-[#FFA500] font-bold ml-1.5">Save 38%</span> on annual ·
        <span className="hidden sm:inline text-zinc-400 ml-1.5">All-Access bundle covers Sports + Stocks + Options</span>
      </>
    ),
  },
  {
    id: "b",
    label: "Price + Cancel",
    body: (
      <>
        <span className="font-bold">$25/wk</span> · cancel anytime ·
        <span className="text-[#FFA500] font-bold ml-1.5">7-day free</span> · no card charge until day 8 ·
        <span className="hidden sm:inline text-zinc-400 ml-1.5">3 markets, 1 subscription</span>
      </>
    ),
  },
  {
    id: "c",
    label: "Track-record + Trial",
    body: (
      <>
        <span className="font-bold">~70% MLB hit rate</span> ·
        <span className="text-[#FFA500] font-bold ml-1.5">Tamper-evident</span> predictions · locked before first pitch ·
        <span className="hidden sm:inline text-zinc-400 ml-1.5">Try 7 days free, cancel any time</span>
      </>
    ),
  },
];

function pickHotVariant() {
  try {
    const cached = window.localStorage.getItem("hot.variant");
    if (cached && HOT_VARIANTS.some((v) => v.id === cached)) {
      return HOT_VARIANTS.find((v) => v.id === cached);
    }
    const choice = HOT_VARIANTS[Math.floor(Math.random() * HOT_VARIANTS.length)];
    window.localStorage.setItem("hot.variant", choice.id);
    return choice;
  } catch {
    return HOT_VARIANTS[0];
  }
}

export default function ProductLanding() {
  const variant = React.useMemo(pickHotVariant, []);
  return (
    <div className="min-h-[calc(100vh-180px)]" data-testid="product-landing">
      {/* HOT promo banner — A/B-tested copy, drives subscription clicks from the top of the funnel */}
      <Link to="/pricing" data-testid="landing-hot-promo" data-variant={variant.id} className="block relative overflow-hidden border-y border-[#FF3B30]/30 bg-gradient-to-r from-[#FF3B30]/10 via-[#FFA500]/10 to-[#FF3B30]/10 hover:from-[#FF3B30]/20 hover:via-[#FFA500]/20 hover:to-[#FF3B30]/20 transition-colors group">
        <div className="max-w-[1280px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <motion.div
              initial={{ rotate: -8, scale: 0.9 }}
              animate={{ rotate: [-8, 8, -8], scale: [0.9, 1.05, 0.9] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="flex-shrink-0"
            >
              <Flame size={22} weight="fill" className="text-[#FF6B35]" />
            </motion.div>
            <div className="inline-flex items-center gap-2 px-2 py-0.5 bg-[#FF3B30] text-black text-[10px] font-bold uppercase tracking-widest rounded-sm">HOT</div>
            <p className="text-sm text-zinc-100 truncate">{variant.body}</p>
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#22C55E] hover:bg-[#16A34A] text-black text-xs uppercase tracking-wider font-bold rounded-sm group-hover:translate-x-0.5 transition-transform flex-shrink-0">
            See plans <ArrowRight size={12} weight="bold" />
          </div>
        </div>
      </Link>

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-3">
            <ShieldCheck size={12} weight="fill" /> Tamper-evident · Locked predictions · Before the action
          </div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] mb-5">
            TWO ANALYSTS.<br />
            <span className="text-zinc-500">SPORTS</span> <span className="text-white">& FINANCE.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Signal-driven predictions across MLB, WNBA, stocks, and options.
            Every call is timestamped and locked before the action starts —
            no hindsight bias, no cherry-picking, no revisions.
          </p>
        </motion.div>
      </section>

      {/* Product cards */}
      <section className="max-w-[1280px] mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {PRODUCTS.map((p, i) => <ProductCard key={p.id} p={p} idx={i} />)}
        </div>
      </section>

      {/* Bundle banner */}
      <section className="max-w-[1280px] mx-auto px-6 pb-16">
        <div className="bg-gradient-to-r from-[#007AFF]/10 via-[#F59E0B]/10 to-[#A855F7]/10 border border-white/20 rounded-sm p-6 sm:p-8" data-testid="bundle-banner">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white mb-2 inline-flex items-center gap-1.5">
                <Lock size={11} weight="fill" /> Bundle
              </div>
              <h3 className="font-display text-4xl sm:text-5xl leading-none">{BUNDLE.label}</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-2xl">{BUNDLE.description}</p>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl sm:text-5xl">{BUNDLE.price}<span className="text-zinc-500 text-xl"> /{BUNDLE.cadence}</span></div>
              <Link
                to="/markets/all-access"
                data-testid="bundle-cta"
                className="inline-flex items-center gap-1.5 mt-3 px-5 py-2.5 bg-white text-black text-[11px] uppercase tracking-[0.2em] font-bold rounded-sm hover:bg-zinc-200"
              >
                Join Bundle Waitlist <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-[1280px] mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="border border-white/10 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#22C55E] mb-2">Tamper-Evident</div>
            <div className="text-zinc-300 leading-relaxed">Every prediction is timestamped and locked into an immutable ledger before the action starts.</div>
          </div>
          <div className="border border-white/10 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#007AFF] mb-2">7-Day Free Trial</div>
            <div className="text-zinc-300 leading-relaxed">No charge for the first 7 days on any subscription. Cancel anytime from your account.</div>
          </div>
          <div className="border border-white/10 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A855F7] mb-2">Public Track Record</div>
            <div className="text-zinc-300 leading-relaxed">Every prediction ever published sits in the receipts ledger. No edits. No deletions. Nowhere to hide.</div>
          </div>
        </div>
      </section>
    </div>
  );
}
